import {
  FilterOutlined,
  SearchOutlined,
  FilterFilled,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { message, Table, Icon, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableSearchDate,
  TableSearchInput,
  TableAsyncSelect,
  DropFilterIcon,
} from 'common/components/table-filter-component';
import { PartnerEmpty as PartnerEmptyIcon } from 'common/components/svgs';
import { PartnerType } from '~constants/partner';
import {
  QUERY_PARTNER_CONTACTS,
  QUERY_LIST_REFERRERS,
} from '~pages/dashboard/referral/partner/queries';
import { dayFormatType, formatDay, moment } from '~helpers/day';
import FilterDropdown from 'common/components/filter';
import { useHistory } from 'react-router-dom';
import generatePath from '~settings/routing';
import { useCurrentRoles } from '~components/switch-role/useSwitchRole';
import PartnerReassignEditorContainer from './partner-reassign-editor/index';
import useExportPartner from './export';

/**
 *
 * @param {string|null|undefined} text need trim text.
 * @returns {string|null}
 */
const removeSpace = text => {
  if (text) {
    return text.trim() || null;
  }
  return text;
};

/**
 * build user name
 * @param {string} firstName
 * @param {string} lastName
 * @returns string
 */
function userNameExtract(firstName, lastName) {
  const insideFirstName = firstName || '';
  const insideLastName = lastName || '';
  if (!insideFirstName.trim() && !insideLastName.trim()) {
    return '-';
  }
  return `${insideFirstName} ${insideLastName}`.trim();
}
const CHECKED_COLOR = '#38b2a6';
const UNCHECKED_COLOR = '#979797';

const partnerType = {
  INDIVIDUAL: 'INDIVIDUAL',
  EDUCATION_AGENT: 'EDUCATION_AGENT',
  UNIVERSITY_INCLUDING_FEEDER: 'UNIVERSITY_INCLUDING_FEEDER',
  STUDENT_ASSOCIATION: 'STUDENT_ASSOCIATION',
  ONLINE: 'ONLINE',
  OTHER: 'OTHER',
  MARKETING_MEDIA_PARTNER: 'MARKETING_MEDIA_PARTNER',
  GLOBAL_EDUCATION_PARTNER: 'GLOBAL_EDUCATION_PARTNER',
  THIRD_PARTY_PARTNER_EASY_TRANSFER_USA: 'THIRD_PARTY_PARTNER_EASY_TRANSFER_USA',
  UNIVERSITY_STUDENT_AMBASSADOR: 'UNIVERSITY_STUDENT_AMBASSADOR',
  EDUCATION_PARTNER: 'EDUCATION_PARTNER',
  THIRD_PARTY_PARTNER_HOOLI_HOME: 'THIRD_PARTY_PARTNER_HOOLI_HOME',
  THIRD_PARTY_EDUCATION_RELATED_SERVICE_PROVIDER: 'THIRD_PARTY_EDUCATION_RELATED_SERVICE_PROVIDER',
  THIRD_PARTY_PARTNER_STUDAPART: 'THIRD_PARTY_PARTNER_STUDAPART',
  CHAT_GROUP: 'CHAT_GROUP',
  SOCIAL: 'SOCIAL',
  THIRD_PARTY_COMMERCIAL_PARTNERS: 'THIRD_PARTY_COMMERCIAL_PARTNERS',
};

const availableCountries = [
  {
    name: 'China',
    countryCode: 'CHN',
  },
  {
    name: 'India',
    countryCode: 'IND',
  },
  {
    name: 'Singapore',
    countryCode: 'SGP',
  },
  {
    name: 'Malaysia',
    countryCode: 'MYS',
  },
  {
    name: 'Indonesia',
    countryCode: 'IDN',
  },
  {
    name: 'Thailand',
    countryCode: 'THA',
  },
];

const Partner = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { authRole } = useCurrentRoles();

  const [selectedRows, setSelectedRows] = useState([]);

  const [showPartnerNameFilter, setShowPartnerNameFilter] = useState(false);
  const [showPartnerCodeFilter, setShowPartnerCodeFilter] = useState(false);
  const [showPhoneNumberFilter, setShowPhoneNumberFilter] = useState(false);
  const [showCreatedTimeFilter, setShowCreatedTimeFilter] = useState(false);
  const [showReferralIdFilter, setShowReferralIdFilter] = useState(false);
  const [showEmailAddressFilter, setShowEmailAddressFilter] = useState(false);
  const [showCountryFilter, setShowCountryFilter] = useState(false);
  const [searchReferrerName, setSearchReferrerName] = useState(null);
  const [typeVisible, setTypeVisible] = useState(false);

  const [showReassignEditor, setShowReassignEditor] = useState(false);

  const [queryFilter, setQueryFilter] = useState({
    partnerName: null,
    partnerCode: null,
    phoneNumber: null,
    referrerId: null,
    emailAddress: null,
    partnerTypes: null,
    createdTime: [],
    countries: [],
    enabled: null,
  });

  const partnerTypesOpts = useMemo(() => {
    return Object.keys(partnerType).map(type => ({
      text: t(`cms.partner.partner_type.${type}`),
      value: partnerType[type],
    }));
  }, []);

  const isValidFilter = useMemo(() => {
    return (
      queryFilter.partnerCode ||
      queryFilter.partnerName ||
      queryFilter.phoneNumber ||
      queryFilter.referrerId ||
      queryFilter.emailAddress ||
      !queryFilter.partnerTypes ||
      queryFilter.partnerTypes.length ||
      queryFilter.createdTime.length ||
      queryFilter.countries.length ||
      queryFilter.enabled
    );
  }, [queryFilter]);

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const { loading, data: partnerListResponse } = useQuery(QUERY_PARTNER_CONTACTS, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: 'UPDATED_AT',
      sortDirection: 'DESCENDING',
      partnerName: queryFilter.partnerName || null,
      partnerCode: queryFilter.partnerCode || null,
      partnerReferrerId: queryFilter.referrerId || null,
      partnerTypes: queryFilter.partnerTypes || null,
      phoneNumber: queryFilter.phoneNumber || null,
      createdAfter: queryFilter.createdTime.length ? queryFilter.createdTime[0] : null,
      createdBefore: queryFilter.createdTime.length ? queryFilter.createdTime[1] : null,
      emailAddress: queryFilter.emailAddress || null,
      countries: queryFilter.countries.length
        ? queryFilter.countries
        : ['CHN', 'IND', 'SGP', 'MYS', 'IDN', 'THA'],
      enables: typeof queryFilter.enabled === 'boolean' ? [queryFilter.enabled] : null,
    },
    fetchPolicy: 'cache-and-network',
    onError: e => {
      message.error(e?.toString());
    },
  });

  const { loading: referrerListLoading, data: referrerListResponse } = useQuery(
    QUERY_LIST_REFERRERS,
    {
      variables: {
        nameLike: searchReferrerName || null,
        pageNumber: 1,
        pageSize: 100,
      },
    },
  );

  const referrerList = useMemo(() => {
    return (
      referrerListResponse?.referrers?.edges.map(edge => ({
        label: `${edge?.node?.firstName || ''} ${edge?.node?.lastName || ''}`,
        value: edge.node.id,
      })) || []
    );
  }, [referrerListResponse]);

  const isDisabledDate = useCallback(current => {
    // Can not select days which after today or before 2022.1.1
    return (
      current && (current > moment().endOf('day') || current < moment('2022-1-1').startOf('day'))
    );
  }, []);

  const partnerList = useMemo(() => {
    return partnerListResponse?.partnerContacts?.edges.map(edge => edge.node) || [];
  }, [partnerListResponse]);

  const listTotalCount = useMemo(() => {
    return partnerListResponse?.partnerContacts?.totalCount || 0;
  }, [partnerListResponse]);

  const { exportToCSV, isLoading: isExportPartnerLoading } = useExportPartner();

  const handleRowSelect = (_keys, rows) => {
    const currentPageSelectKeys = rows.map(row => row.partner.id);

    const notSelectRows = partnerList
      .filter(partnerNode => !currentPageSelectKeys.includes(partnerNode.partner.id))
      .map(item => item.partner.id);
    setSelectedRows(current => [
      ...new Set(
        current.concat(currentPageSelectKeys).filter(item => !notSelectRows.includes(item)),
      ),
    ]);
  };

  const handleReassignPartner = () => {
    setShowReassignEditor(true);
  };

  const handleCloseEditorModal = () => {
    setShowReassignEditor(false);
  };

  const tableColumns = useMemo(() => {
    return [
      {
        title: t('cms.referral.partner.list.column.name'),
        dataIndex: 'partner.name',
        key: 'name',
        className: 'partner-page__body__list__column',
        width: 150,
        fixed: 'left',
        render: text => (
          <span
            title={text}
            className="partner-page__body__list__column-body"
            style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
          >
            {text || '-'}
          </span>
        ),
        filterDropdown: (
          <div className="partner__body__list__columns-dropdown">
            <TableSearchInput
              field="partnerName"
              value={queryFilter.partnerName || ''}
              onChange={(_fieldName, value) => {
                setQueryFilter(current => ({ ...current, partnerName: removeSpace(value) }));
                setPagination(current => ({ ...current, pageNumber: 1 }));
                setShowPartnerNameFilter(false);
              }}
            />
          </div>
        ),
        filterIcon: (
          <SearchOutlined style={{ color: queryFilter.partnerName ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: showPartnerNameFilter,
        onFilterDropdownVisibleChange: view => {
          setShowPartnerNameFilter(view);
        },
      },
      {
        title: t('cms.referral.partner.list.column.type'),
        dataIndex: 'partner.type',
        key: 'type',
        className: 'partner-page__body__list__column',
        width: 150,
        render: text => (
          <span title={text} className="partner-page__body__list__column-body">
            {PartnerType[text] || '-'}
          </span>
        ),
        filterDropdown: (
          <FilterDropdown
            filterDropdownVisible={typeVisible}
            options={partnerTypesOpts.map(item =>
              Object.assign({ value: item.value }, { label: item.text }),
            )}
            closeBox={() => {
              setTypeVisible(false);
            }}
            submitHandle={types => {
              setQueryFilter(current => ({
                ...current,
                partnerTypes: types.length > 0 ? types : null,
              }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
            }}
          />
        ),
        filterIcon: (
          <span className="ant-table-filter-icon">
            <DropFilterIcon
              color={queryFilter.partnerTypes?.length > 0 ? CHECKED_COLOR : UNCHECKED_COLOR}
            />
          </span>
        ),
        filterDropdownVisible: typeVisible,
        onFilterDropdownVisibleChange: visible => {
          setTypeVisible(visible);
        },
      },
      {
        title: (
          <div>
            {t('cms.referral.partner.list.column.status')}
            <Tooltip title={t('cms.referral.partner.list.tooltip.status')} placement="top">
              <QuestionCircleOutlined className="partner-page__body__list__column-header__tooltip__icon" />
            </Tooltip>
          </div>
        ),
        dataIndex: 'enabled',
        key: 'status',
        className: 'partner-page__body__list__column',
        width: 150,
        render: text => (
          <span title={text} className="partner-page__body__list__column-body">
            {text
              ? t('cms.referral.partner.list.column.status.enabled')
              : t('cms.referral.partner.list.column.status.disabled')}
          </span>
        ),
        filters: [
          { text: t('cms.referral.partner.list.column.status.enabled'), value: 'true' },
          { text: t('cms.referral.partner.list.column.status.disabled'), value: 'false' },
        ],
        filteredValue:
          typeof queryFilter.enabled === 'boolean' ? [String(queryFilter.enabled)] : null,
        filterMultiple: false,
        filterIcon: (
          <FilterFilled
            style={{
              color: typeof queryFilter.enabled === 'boolean' ? '#38b2a6' : '#c8c9cb',
            }}
          />
        ),
      },
      {
        title: (
          <div className="partner-page__body__list__column-header">
            {t('cms.referral.partner.list.column.number')}
          </div>
        ),
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        className: '',
        width: 180,
        render: text => (
          <span title={text} className="partner-page__body__list__column-body">
            {text
              ? (() => {
                  const firstPart = text.split(' ')[0];
                  const resetPart = text.split(' ');
                  resetPart.shift();
                  return firstPart.concat(' ').concat(resetPart.join('').replaceAll(' ', ''));
                })()
              : '-'}
          </span>
        ),
        filterDropdown: (
          <div className="partner__body__list__columns-dropdown">
            <TableSearchInput
              field="phoneNumber"
              value={queryFilter.phoneNumber || ''}
              onChange={(_fieldName, value) => {
                setQueryFilter(current => ({ ...current, phoneNumber: removeSpace(value) }));
                setPagination(current => ({ ...current, pageNumber: 1 }));
                setShowPhoneNumberFilter(false);
              }}
            />
          </div>
        ),
        filterIcon: (
          <SearchOutlined style={{ color: queryFilter.phoneNumber ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: showPhoneNumberFilter,
        onFilterDropdownVisibleChange: view => {
          setShowPhoneNumberFilter(view);
        },
      },
      {
        title: (
          <div className="partner-page__body__list__column-header">
            {t('cms.referral.partner.list.column.email')}
          </div>
        ),
        dataIndex: 'emailAddress',
        key: 'emailAddress',
        className: '',
        width: 220,
        render: text => (
          <span title={text} className="partner-page__body__list__column-body">
            {text || '-'}
          </span>
        ),
        filterDropdown: (
          <div className="partner__body__list__columns-dropdown">
            <TableSearchInput
              field="emailAddress"
              value={queryFilter.emailAddress || ''}
              onChange={(_fieldName, value) => {
                setQueryFilter(current => ({ ...current, emailAddress: removeSpace(value) }));
                setPagination(current => ({ ...current, pageNumber: 1 }));
                setShowEmailAddressFilter(false);
              }}
            />
          </div>
        ),
        filterIcon: (
          <SearchOutlined style={{ color: queryFilter.emailAddress ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: showEmailAddressFilter,
        onFilterDropdownVisibleChange: view => {
          setShowEmailAddressFilter(view);
        },
      },
      {
        title: (
          <div className="partner-page__body__list__column-header">
            {t('cms.referral.partner.list.column.country')}
          </div>
        ),
        dataIndex: 'partner.originCountry.name',
        key: 'country',
        width: 128,
        className: 'partner-page__body__list__column',
        filterDropdown: (
          <FilterDropdown
            filterDropdownVisible={showCountryFilter}
            options={availableCountries.map(c => ({ label: c.name, value: c.countryCode }))}
            closeBox={() => {
              setTypeVisible(false);
            }}
            submitHandle={value => {
              setQueryFilter(current => ({
                ...current,
                countries: value.length > 0 ? value : [],
              }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowCountryFilter(false);
            }}
          />
        ),
        filterDropdownVisible: showCountryFilter,
        onFilterDropdownVisibleChange: visible => {
          setShowCountryFilter(visible);
        },
        filterIcon: (
          <FilterOutlined style={{ color: queryFilter.countries.length ? '#38b2a6' : '#c8c9cb' }} />
        ),
        render: text => (
          <span className="partner-page__body__list__column-body">{text || '-'}</span>
        ),
      },
      {
        title: (
          <div className="partner-page__body__list__column-header">
            {t('cms.referral.partner.list.column.partner_code')}
          </div>
        ),
        dataIndex: 'partner.code',
        key: 'partnerCode',
        className: 'partner-page__body__list__column',
        width: 140,
        render: text => (
          <span className="partner-page__body__list__column-body">{text || '-'}</span>
        ),
        filterDropdown: (
          <TableSearchInput
            field="partnerCode"
            value={queryFilter.partnerCode || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, partnerCode: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowPartnerCodeFilter(false);
            }}
          />
        ),
        filterIcon: (
          <SearchOutlined style={{ color: queryFilter.partnerCode ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: showPartnerCodeFilter,
        onFilterDropdownVisibleChange: view => {
          setShowPartnerCodeFilter(view);
        },
      },
      {
        title: t('cms.referral.partner.list.column.commission_plan'),
        dataIndex: 'partner.commissionPlan.name',
        key: 'commissionPlanName',
        className: 'partner-page__body__list__column',
        width: 180,
        render: text => (
          <span className="partner-page__body__list__column-body">{text || '-'}</span>
        ),
      },
      {
        title: t('cms.referral.partner.list.column.payout_plan'),
        dataIndex: 'partner.payoutPlan.name',
        key: 'payoutPlanName',
        className: '',
        width: 200,
        render: text => {
          return <span className="partner-page__body__list__column-body">{text || '-'}</span>;
        },
      },
      {
        title: t('cms.referral.partner.list.column.referrer_name'),
        dataIndex: 'partner.internalReferrer.name',
        key: 'internalReferrer',
        className: 'partner-page__body__list__column',
        width: 162,
        render: (_text, record) => {
          const firstName = record.partner.internalReferrer?.firstName || '';
          const lastName = record.partner.internalReferrer?.lastName || '';
          return (
            <span className="partner-page__body__list__column-body">
              {userNameExtract(firstName, lastName)}
            </span>
          );
        },
        filterDropdown: showReferralIdFilter ? (
          <TableAsyncSelect
            field="referrerId"
            value={queryFilter.referrerId ? [queryFilter.referrerId] : []}
            list={referrerList}
            onInput={value => setSearchReferrerName(value)}
            fetching={referrerListLoading}
            onChange={(_fieldName, value) => {
              const referrerId = value.length ? value[0] : null;
              setQueryFilter(current => ({ ...current, referrerId: referrerId }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowReferralIdFilter(false);
            }}
          />
        ) : (
          <div />
        ),
        filterIcon: (
          <SearchOutlined style={{ color: queryFilter.referrerId ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: showReferralIdFilter,
        onFilterDropdownVisibleChange: view => {
          setShowReferralIdFilter(view);
          if (!view) {
            setSearchReferrerName(null);
          }
        },
      },
      {
        title: t('cms.referral.partner.list.column.signup_date'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        className: 'partner-page__body__list__column partner-page__body__list__column--min-w-180',
        render: text => {
          return (
            <span className="partner-page__body__list__column-body partner-page__body__list__column--min-w-180">
              {formatDay(dayFormatType.UKWITHHM, text) || '-'}
            </span>
          );
        },
        filterDropdown: (
          <TableSearchDate
            field="createdTime"
            value={queryFilter.createdTime || []}
            disabledDate={current => isDisabledDate(current)}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, createdTime: value }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowCreatedTimeFilter(false);
            }}
          />
        ),
        filterIcon: (
          <SearchOutlined
            style={{ color: queryFilter.createdTime.length ? '#38b2a6' : '#c8c9cb' }}
          />
        ),
        filterDropdownVisible: showCreatedTimeFilter,
        onFilterDropdownVisibleChange: view => {
          setShowCreatedTimeFilter(view);
        },
      },
      {
        title: t('cms.referral.partner.list.column.actions'),
        dataIndex: 'actions',
        key: 'actions',
        width: 80,
        fixed: 'right',
        render: (_text, record) => (
          <div className="partner-page__body__list__column-body">
            <a
              role="button"
              tabIndex="0"
              onClick={() =>
                history.push(generatePath('referral.partnerDetail', { id: record.partner.code }))
              }
            >
              {t('cms.form.label.view')}
            </a>
          </div>
        ),
      },
    ];
  }, [
    showPartnerNameFilter,
    showPartnerCodeFilter,
    showPhoneNumberFilter,
    showReferralIdFilter,
    showCreatedTimeFilter,
    showEmailAddressFilter,
    queryFilter,
    referrerList,
    typeVisible,
    showCountryFilter,
    referrerListLoading,
  ]);

  const handleExportCsv = () => {
    exportToCSV({ queryFilter, totalCount: listTotalCount });
  };

  return (
    <>
      <div className="partner-page">
        <div className="partner-page__header">
          <h1>{t('cms.referral.partner.title')}</h1>
          <div className="partner-page__header__control-area">
            <button
              type="button"
              className={classNames(
                'partner-page__header__button partner-page__header__button--default',
                {
                  'partner-page__header__button--disabled': isExportPartnerLoading,
                },
              )}
              onClick={handleExportCsv}
            >
              <Choose>
                <When condition={isExportPartnerLoading}>
                  <Spin
                    className="partner-page__header__button__icon"
                    indicator={<Icon type="loading" style={{ fontSize: 14 }} spin />}
                  />
                </When>
                <Otherwise>
                  <Icon className="partner-page__header__button__icon" type="export" />
                </Otherwise>
              </Choose>
              {t('cms.referral.partner.header.btn.export')}
            </button>
            <If condition={selectedRows.length}>
              <button
                type="button"
                className="partner-page__header__button partner-page__header__button--primary"
                onClick={handleReassignPartner}
              >
                {t('cms.referral.partner.header.btn.reassign')}
              </button>
            </If>
          </div>
        </div>

        <section
          className={classNames('partner-page__body', {
            'partner-page__body--bg-white': partnerList.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={partnerList.length || isValidFilter || loading}>
              <Table
                rowKey={record => record.id}
                columns={tableColumns}
                className="table-list partner-page__body__list"
                dataSource={partnerList}
                {...(authRole !== 'bd'
                  ? {
                      rowSelection: {
                        onChange: handleRowSelect,
                        columnWidth: 44,
                      },
                    }
                  : null)}
                pagination={{
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  total: listTotalCount,
                }}
                loading={loading}
                onChange={(newestPagination, filter) => {
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  });
                  setQueryFilter(current => ({
                    ...current,
                    enabled: filter.status?.length ? filter.status[0] === 'true' || false : null,
                  }));
                }}
              />
            </When>
            <When condition={partnerList.length === 0 && !isValidFilter && !loading}>
              <div className="partner-page__blank-tips">
                <div className="partner-page__blank-tips__content">
                  <div>
                    <PartnerEmptyIcon />
                  </div>
                  <span className="partner-page__blank-tips__tip">
                    {t('cms.referral.partner.blanktip')}
                  </span>
                </div>
              </div>
            </When>
          </Choose>
        </section>

        <If condition={showReassignEditor}>
          <PartnerReassignEditorContainer
            activeModal
            t={t}
            selectedPartners={selectedRows}
            handleCloseModal={handleCloseEditorModal}
          />
        </If>
      </div>
    </>
  );
};

export default Partner;
